import {
  useLayoutEffect,
  useState,
  type CSSProperties,
  type RefObject,
} from "react";

const GAP = 6;
const MARGIN = 8;
const HIDDEN: CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  visibility: "hidden",
};

/** The visible part of the nearest scrolling area around `el` (the message list), within the window. */
function visibleArea(el: HTMLElement, view: Window) {
  for (let parent = el.parentElement; parent; parent = parent.parentElement) {
    const overflow = view.getComputedStyle(parent).overflowY;
    if (overflow === 'auto' || overflow === 'scroll') {
      const r = parent.getBoundingClientRect();
      return { top: Math.max(0, r.top), bottom: Math.min(view.innerHeight, r.bottom) };
    }
  }
  return { top: 0, bottom: view.innerHeight };
}

/**
 * Places a panel next to its button in fixed (window) coordinates, so a scrolling list never clips
 * it. It opens where it fits inside the list the button is in (below first, then above), so it does
 * not cover what is around the list, like the composer; when it fits on neither side there, it
 * takes the side with more room in the window. Right edges are aligned and kept on screen. It
 * follows the button on scroll and resize, and again when its own size changes (a menu turning into
 * a longer list); `maxHeight` lets a panel too tall for either side scroll inside. Render the panel
 * in a portal (document body) with this style.
 */
export function useFloating(
  open: boolean,
  button: RefObject<HTMLElement | null>,
  panel: RefObject<HTMLElement | null>,
): CSSProperties {
  const [style, setStyle] = useState<CSSProperties>(HIDDEN);

  useLayoutEffect(() => {
    if (!open) {
      setStyle(HIDDEN);
      return;
    }
    const view = button.current?.ownerDocument.defaultView ?? window;
    const place = () => {
      const anchor = button.current;
      const box = panel.current;
      if (!anchor || !box) return;
      const r = anchor.getBoundingClientRect();
      const width = box.offsetWidth;
      const height = box.scrollHeight;
      const area = visibleArea(anchor, view);
      const fitsBelow = height <= area.bottom - r.bottom - GAP;
      const fitsAbove = height <= r.top - area.top - GAP;
      const below = view.innerHeight - r.bottom - GAP - MARGIN;
      const above = r.top - GAP - MARGIN;
      const down = fitsBelow || (!fitsAbove && below >= above);
      const room = Math.max(0, down ? below : above);
      const top = down ? r.bottom + GAP : r.top - GAP - Math.min(height, room);
      const left = Math.min(
        Math.max(MARGIN, r.right - width),
        view.innerWidth - width - MARGIN,
      );
      setStyle((before) =>
        before.top === top &&
        before.left === left &&
        before.maxHeight === room &&
        before.visibility === "visible"
          ? before
          : {
              position: "fixed",
              top,
              left,
              maxHeight: room,
              visibility: "visible",
            },
      );
    };
    place();
    const resized = new ResizeObserver(place);
    if (panel.current) resized.observe(panel.current);
    view.addEventListener("resize", place);
    // Any scrolling container (the message list) moves the button.
    view.addEventListener("scroll", place, true);
    return () => {
      resized.disconnect();
      view.removeEventListener("resize", place);
      view.removeEventListener("scroll", place, true);
    };
  }, [open, button, panel]);

  return style;
}
